import { useCallback, useEffect, useState, useRef } from 'react'
import { PaymentMethod } from 'models/ChargeIntent'
import { getAnalyticsInstance } from 'modules/analytics/analytics'
import {
  getPaymentMethodConfig,
  useActivePaymentMethodConfigs,
} from '../utils/use-payment-method-configs'
import useTrackUIElementEvent from 'modules/analytics/hooks/useTrackUIElementEvent'
import mParticleSdk from '@mparticle/web-sdk'
import useOrder from '../../../../order/state/order/use-order'

export const usePaymentMethodSelection = () => {
  const order = useOrder()
  const analytics = getAnalyticsInstance()
  const [isLoading, setIsLoading] = useState(true)
  const hasInitializedRef = useRef(false)
  const walletType = order.stripePaymentRequest.walletType

  const selectedPaymentMethodConfig = getPaymentMethodConfig(order.paymentDetails.type, walletType)

  const activePaymentMethodConfigs = useActivePaymentMethodConfigs()
  const trackUIElementEvent = useTrackUIElementEvent()

  // Helper function to get human-readable payment method name
  const getPaymentMethodDisplayName = useCallback(
    (paymentMethod: PaymentMethod): string => {
      const config = activePaymentMethodConfigs.find((config) => config.key === paymentMethod)

      return config?.label || paymentMethod
    },
    [activePaymentMethodConfigs],
  )

  // Initialize payment method state without triggering user interaction logic
  const initializePaymentMethod = useCallback(
    (value: PaymentMethod) => {
      const config = activePaymentMethodConfigs.find((config) => config.key === value)

      if (config && config.processor) {
        // Dispatch actions to set the payment type and processor
        order.paymentDetails.setType(value)
        order.paymentDetails.setProcessor(config.processor)

        // Track payment processor in analytics storage
        if (analytics && analytics.storage) {
          analytics.storage.setItem('paymentProcessor', config.processor)
        }
      } else {
        console.error(`No valid processor found for payment method: ${value}`)
      }
    },
    [activePaymentMethodConfigs, analytics, order.paymentDetails],
  )

  // Handle user selection of payment methods (actual user interactions only)
  const onSelect = useCallback(
    (value: PaymentMethod) => {
      const config = activePaymentMethodConfigs.find((config) => config.key === value)

      if (config && config.processor) {
        // Track every user interaction
        trackUIElementEvent('Value Selected', mParticleSdk.EventType.Other, {
          tracking_id: 'donation payment method',
          value: getPaymentMethodDisplayName(value),
        })

        // Dispatch actions to set the payment type and processor
        order.paymentDetails.setType(value)
        order.paymentDetails.setProcessor(config.processor)

        // Track payment processor in analytics storage
        if (analytics && analytics.storage) {
          analytics.storage.setItem('paymentProcessor', config.processor)
        }
      } else {
        console.error(`No valid processor found for payment method: ${value}`)
      }
    },
    [
      activePaymentMethodConfigs,
      trackUIElementEvent,
      getPaymentMethodDisplayName,
      order.paymentDetails,
      analytics,
    ],
  )

  // Monitor the active payment methods and set loading state accordingly
  useEffect(() => {
    if (activePaymentMethodConfigs.length > 0) {
      setIsLoading(false)
    }
  }, [activePaymentMethodConfigs])

  // Initialize the selected payment method after loading
  // (without triggering user interaction logic)
  useEffect(() => {
    if (!isLoading && order.paymentDetails.type && !hasInitializedRef.current) {
      initializePaymentMethod(order.paymentDetails.type)
      hasInitializedRef.current = true
    }
  }, [isLoading, order.paymentDetails.type, initializePaymentMethod])

  return {
    onSelect,
    isLoading,
    selectedPaymentMethod: order.paymentDetails.type,
    selectedPaymentMethodConfig,
    activePaymentMethodConfigs,
  }
}
